import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Container, CssBaseline, ThemeProvider,
  Paper, TextField, Button, CircularProgress, Box, Divider, Alert, Link,
  Radio, RadioGroup, FormControlLabel
} from '@mui/material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import Footer from '../pages/Footer';
import logo from '../logo.png';
import axios from 'axios';
import UserProfileEditor from './UserProfileEditor';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(18,82,166)',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
  },
});

export default function UpdatePreferencePage() {
  const [transactionNumber, setTransactionNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [student, setStudent] = useState(null);
  const [conflictingProfiles, setConflictingProfiles] = useState([]);
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(null);
  const [enableTicketDownload, setEnableTicketDownload] = useState(false);
  const [enableProfileView, setEnableProfileView] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  // Check if profile view is enabled - redirect if not
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get('https://exam.ethernet.edu.et/api/registration-status');
        if (res.data) {
          const profileViewEnabled = res.data.enable_profileview || false;
          const ticketDownloadEnabled = res.data.enable_ticket_download || false;
          
          setEnableProfileView(profileViewEnabled);
          setEnableTicketDownload(ticketDownloadEnabled);
          
          // If profile view is disabled, redirect to registration page
          if (!profileViewEnabled) {
            navigate('/registration', { replace: true });
            return;
          }
        } else {
          // If no data, assume access denied
          navigate('/registration', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Failed to fetch registration status', error);
        // On error, redirect to registration page
        navigate('/registration', { replace: true });
        return;
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, [navigate]);

  const handleVerify = async () => {
    if (!transactionNumber) {
      setError('Transaction number is required.');
      return;
    }

    if (!recaptchaRef.current) {
      setError('Captcha is not ready. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError('');
    setConflictingProfiles([]);
    setSelectedProfileIndex(null);

    try {
      let recaptchaToken = null;

      try {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();
      } catch (captchaError) {
        console.error('reCAPTCHA execution error:', captchaError);
        setError('Too many attempts or a browser issue. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const res = await axios.post('https://exam.ethernet.edu.et/api/verify-transaction', {
        transaction_number: transactionNumber,
        recaptcha_token: recaptchaToken,
      });

      setStudent(res.data);
    } catch (err) {
      const responseError = err.response?.data;
      console.error('Server error:', err);

      if (err.response?.status === 409 && responseError?.conflicting_profiles) {
        setConflictingProfiles(responseError.conflicting_profiles);
        setError(responseError.error || 'Account conflict detected.');
      } else {
        setError(responseError?.error || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelection = async () => {
    const selected = conflictingProfiles[selectedProfileIndex];
    if (!selected) return;
  
    try {
      setLoading(true);
      await axios.post('https://exam.ethernet.edu.et/api/resolve-fayda-conflict', {
        transaction_number: transactionNumber,
        name: selected.name,
        gender: selected.gender,
        birthdate: selected.birthdate,
        sub: selected.sub,
      });
  
      // Re-attempt transaction fetch to load clean result
      await handleVerify();
    } catch (err) {
      console.error('Conflict resolution failed:', err);
      setError('Could not resolve conflict. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  

  // Show loading while checking access
  if (checkingAccess) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <div className="page-container">
          <CssBaseline />
          <AppBar position="static">
            <Toolbar>
              <img src={logo} alt="Ministry of Education Logo" style={{ maxHeight: '70px', marginRight: '20px' }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Ministry of Education
              </Typography>
            </Toolbar>
          </AppBar>
          <Container component="main" sx={{ maxWidth: 'lg', py: 4, minHeight: '80vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <CircularProgress />
            </Box>
          </Container>
        </div>
      </ThemeProvider>
    );
  }

  // If profile view is disabled, don't render (redirect will happen)
  if (!enableProfileView) {
    return null;
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <div className="page-container">
        <CssBaseline />
        <AppBar position="static">
          <Toolbar>
            <img src={logo} alt="Ministry of Education Logo" style={{ maxHeight: '70px', marginRight: '20px' }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Ministry of Education
            </Typography>
          </Toolbar>
        </AppBar>

        <Container component="main" sx={{ maxWidth: 'lg', py: 4, minHeight: '80vh' }}>
          {!student && conflictingProfiles.length === 0 ? (
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                maxWidth: 700, 
                mx: 'auto',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BadgeIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1252a6' }}>
                  Access Your Profile
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography sx={{ mb: 3, fontSize: '1rem', fontWeight: 400, color: '#555', lineHeight: 1.7 }}>
                Please enter your <strong style={{ color: '#1252a6' }}>payment transaction number</strong> received from <strong>TeleBirr</strong> during payment to manage your profile and access your exam credentials.
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', color: '#666', fontWeight: 500, mb: 3 }}>
                Need help?{' '}
                <Link
                  href="https://www.youtube.com/@edunews_et"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontWeight: 600, color: '#1976d2' }}
                >
                  Watch this video guide
                </Link>
              </Typography>
              <TextField
                fullWidth
                label="Transaction Number"
                value={transactionNumber}
                onChange={(e) => setTransactionNumber(e.target.value)}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1252a6',
                    },
                  },
                }}
                placeholder="Enter your TeleBirr transaction number"
              />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button 
                  variant="contained" 
                  href="/registration"
                  sx={{ minWidth: 120 }}
                >
                  Back
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleVerify} 
                  disabled={!transactionNumber || loading}
                  sx={{
                    minWidth: 180,
                    py: 1.2,
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify and Continue'}
                </Button>
              </Box>
            </Paper>
          ) : conflictingProfiles.length > 0 ? (
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                maxWidth: 800, 
                mx: 'auto',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Alert severity="warning" sx={{ mb: 3 }}>
                We've detected multiple verified Fayda profiles associated with your Username.
                Please select your correct profile from the list below and click <strong>Continue with this profile</strong>.
              </Alert>

              <RadioGroup
                value={selectedProfileIndex}
                onChange={(e) => setSelectedProfileIndex(parseInt(e.target.value))}
              >
                {conflictingProfiles.map((profile, index) => (
                  <Box key={index} sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: '10px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <FormControlLabel
                      value={index}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography><strong>Name:</strong> {profile.name}</Typography>
                          <Typography><strong>Gender:</strong> {profile.gender}</Typography>
                          <Typography><strong>Birthdate:</strong> {profile.birthdate}</Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>

              <Box
  sx={{
    mt: 3,
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: { xs: 2, sm: 2 },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: { xs: 'stretch', sm: 'space-between' }
  }}
>
  <Button
    variant="contained"
    fullWidth={{ xs: true, sm: false }}
    onClick={() => {
      setConflictingProfiles([]);
      setTransactionNumber('');
    }}
    sx={{ minWidth: { xs: 'auto', sm: 180 } }}
  >
    Try Another Transaction
  </Button>

  <Button
    variant="contained"
    fullWidth={{ xs: true, sm: false }}
    disabled={selectedProfileIndex === null || loading}
    onClick={handleProfileSelection}
    sx={{ minWidth: { xs: 'auto', sm: 200 } }}
  >
    {loading ? <CircularProgress size={24} /> : 'Continue with this profile'}
  </Button>
</Box>

            </Paper>
          ) : (
            <UserProfileEditor student={student} recaptchaRef={recaptchaRef} enableTicketDownload={enableTicketDownload} />
          )}
        </Container>

        <Footer />

        <ReCAPTCHA
          sitekey="6LepLRQrAAAAAGsabsOe4cjcNnm61WG8fuFvuIUq"
          size="invisible"
          ref={recaptchaRef}
        />
      </div>
    </ThemeProvider>
  );
}
